import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Balance,
  BalanceQuery,
  BalancesQueryBuilder,
  ChangeBalanceApplicationErrors,
  ChangeBalanceOptions,
  ChangeBalanceResponse,
  ListBalancesOptions,
  ListBalancesResponse,
  QueryBalancesResponse,
  RevertBalanceChangeApplicationErrors,
  RevertBalanceChangeOptions,
  RevertBalanceChangeResponse,
  changeBalance as universalChangeBalance,
  getBalance as universalGetBalance,
  listBalances as universalListBalances,
  queryBalances as universalQueryBalances,
  revertBalanceChange as universalRevertBalanceChange,
  typedQueryBalances as universalTypedQueryBalances,
} from './benefit-programs-v1-balance-balances.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/benefit-programs' };

export function getBalance(httpClient: HttpClient): GetBalanceSignature {
  return (poolId: string) =>
    universalGetBalance(
      poolId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBalanceSignature {
  /**
   * Retrieves a balance.
   * @param - ID of the pool associated with the balance to retrieve. This is also the ID of the balance.
   * @returns Retrieved balance.
   */
  (poolId: string): Promise<
    NonNullablePaths<
      Balance,
      | `_id`
      | `beneficiary.anonymousVisitorId`
      | `beneficiary.memberId`
      | `beneficiary.wixUserId`
      | `amount.available`
      | `amount.reserved`
      | `poolInfo._id`
      | `poolInfo.status`,
      3
    >
  >;
}

export function listBalances(httpClient: HttpClient): ListBalancesSignature {
  return (options?: ListBalancesOptions) =>
    universalListBalances(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListBalancesSignature {
  /**
   * Retrieves a list of balances.
   */
  (options?: ListBalancesOptions): Promise<
    NonNullablePaths<
      ListBalancesResponse,
      | `balances`
      | `balances.${number}._id`
      | `balances.${number}.beneficiary.anonymousVisitorId`
      | `balances.${number}.beneficiary.memberId`
      | `balances.${number}.beneficiary.wixUserId`
      | `balances.${number}.amount.available`
      | `balances.${number}.amount.reserved`
      | `balances.${number}.poolInfo._id`
      | `balances.${number}.poolInfo.status`,
      5
    >
  >;
}

export function queryBalances(httpClient: HttpClient): QueryBalancesSignature {
  return () =>
    universalQueryBalances(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryBalancesSignature {
  /**
   * Creates a query to retrieve a list of balances.
   *
   * The Query Balances method builds a query to retrieve a list of balances and returns a `BalancesQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `BalancesQueryBuilder` methods onto the query. `BalancesQueryBuilder` methods enable you to filter, sort, and control the results that Query Balances returns.
   *
   * Query Balances has a default paging limit of 50, which you can override.
   *
   *  For a full description of the item object, see the object returned for the `items` property in `BalancesQueryResult`.
   */
  (): BalancesQueryBuilder;
}

export function typedQueryBalances(
  httpClient: HttpClient
): TypedQueryBalancesSignature {
  return (query: BalanceQuery) =>
    universalTypedQueryBalances(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryBalancesSignature {
  /** */
  (query: BalanceQuery): Promise<
    NonNullablePaths<
      QueryBalancesResponse,
      | `balances`
      | `balances.${number}._id`
      | `balances.${number}.beneficiary.anonymousVisitorId`
      | `balances.${number}.beneficiary.memberId`
      | `balances.${number}.beneficiary.wixUserId`
      | `balances.${number}.amount.available`
      | `balances.${number}.amount.reserved`
      | `balances.${number}.poolInfo._id`
      | `balances.${number}.poolInfo.status`,
      5
    >
  >;
}

export function changeBalance(httpClient: HttpClient): ChangeBalanceSignature {
  return (
    poolId: string,
    idempotencyKey: string,
    options?: ChangeBalanceOptions
  ) =>
    universalChangeBalance(
      poolId,
      idempotencyKey,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ChangeBalanceSignature {
  /**
   * Changes the number of available credits in a balance.
   *
   * You can adjust or set the number of available credits.
   * @param - ID of the pool associated with the balance to change. This is also the ID of the balance.
   * @param - Unique identifier, generated by the client.
   * Used to recognize repeated attempts to make the same request.
   */
  (
    poolId: string,
    idempotencyKey: string,
    options?: ChangeBalanceOptions
  ): Promise<
    NonNullablePaths<
      ChangeBalanceResponse,
      | `balance._id`
      | `balance.beneficiary.anonymousVisitorId`
      | `balance.beneficiary.memberId`
      | `balance.beneficiary.wixUserId`
      | `balance.amount.available`
      | `balance.amount.reserved`
      | `balance.poolInfo._id`
      | `balance.poolInfo.status`,
      4
    > & {
      __applicationErrorsType?: ChangeBalanceApplicationErrors;
    }
  >;
}

export function revertBalanceChange(
  httpClient: HttpClient
): RevertBalanceChangeSignature {
  return (
    transactionId: string,
    idempotencyKey: string,
    options?: RevertBalanceChangeOptions
  ) =>
    universalRevertBalanceChange(
      transactionId,
      idempotencyKey,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RevertBalanceChangeSignature {
  /**
   * Reverts a transaction created by calling Change Balance.
   *
   * For example, if a transaction increased a balance's available credits by 5,
   * calling Revert Balance Change with the transaction's ID will decrease the balance's available credit by 5.
   *
   * > **Note:** If the Change Balance call set the balance, Revert Transaction reverts the amount the balance changed by, ensuring subsequent balance changes are still reflected after the reversion.
   * @param - ID of the transaction associated with the balance change to revert.
   * @param - Ignored value, kept for backward compatibility. Idempotency is now handled by the system.
   */
  (
    transactionId: string,
    idempotencyKey: string,
    options?: RevertBalanceChangeOptions
  ): Promise<
    RevertBalanceChangeResponse & {
      __applicationErrorsType?: RevertBalanceChangeApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AdjustOptions,
  ApplicationError,
  Balance,
  BalanceAmount,
  BalanceChangeBalanceRequest,
  BalanceChangeBalanceRequestOperationOneOf,
  BalanceExceededLimits,
  BalanceQuerySpec,
  BalanceType,
  BalancesQueryBuilder,
  BalancesQueryResult,
  BulkActionMetadata,
  BulkChangeBalanceResult,
  BulkChangeBalancesRequest,
  BulkChangeBalancesResponse,
  ChangeAlreadyReverted,
  ChangeBalanceOptions,
  ChangeBalanceOptionsOperationOneOf,
  ChangeBalanceRequest,
  ChangeBalanceRequestOperationOneOf,
  ChangeBalanceRequestType,
  ChangeBalanceResponse,
  CommonIdentificationData,
  CommonIdentificationDataIdOneOf,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetBalanceRequest,
  GetBalanceResponse,
  GetTransactionReversibilityRequest,
  GetTransactionReversibilityResponse,
  HealBalanceRequest,
  HealBalanceResponse,
  HealBalanceTenantRequest,
  HealBalanceTenantResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentityType,
  Item,
  ItemMetadata,
  LimitRule,
  ListBalancesOptions,
  ListBalancesRequest,
  ListBalancesResponse,
  MessageEnvelope,
  NotEnoughBalance,
  PoolInfo,
  PoolStatus,
  QueryBalancesRequest,
  QueryBalancesResponse,
  RestoreInfo,
  RevertBalanceChangeOptions,
  RevertBalanceChangeRequest,
  RevertBalanceChangeResponse,
  RolloverConfigurationInfo,
  SetInitialOptions,
  SetOptions,
  SortOrder,
  Sorting,
  TransactionDetails,
  TransactionReversibility,
  Type,
  WebhookIdentityType,
  utils,
} from './benefit-programs-v1-balance-balances.universal.js';
